import React, { useEffect, useState } from 'react';
import { Coins, X, Loader2, AlertCircle, Receipt, ShieldCheck, Clock4, Download } from 'lucide-react';
import { toast } from 'sonner';

const formatCHF = (n) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
const formatInt = (n) =>
  new Intl.NumberFormat('de-CH').format(Math.round(Number(n) || 0));
const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const STATUS_LABEL = {
  pending: { label: 'In Prüfung', cls: 'bg-amber-500/15 text-amber-300', Icon: Clock4 },
  confirmed: { label: 'Bestätigt', cls: 'bg-emerald-500/15 text-emerald-300', Icon: ShieldCheck },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-500/15 text-red-300', Icon: AlertCircle },
  cancelled: { label: 'Storniert', cls: 'bg-gray-500/15 text-gray-300', Icon: X },
};

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const SubscriptionDialog = ({ open, onClose, onCreated, defaultReferral, shareTypes }) => {
  const enabledTypes = (shareTypes || []).filter((t) => t.enabled);
  const defaultType = enabledTypes[0]?.value || 'namen';
  const [form, setForm] = useState({
    shareCount: '',
    amountCHF: '',
    shareType: defaultType,
    referralCode: defaultReferral || '',
    paymentMethod: 'bank',
    notes: '',
    terms: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setForm((f) => ({
        ...f,
        referralCode: defaultReferral || f.referralCode,
        shareType: f.shareType || defaultType,
      }));
    }
  }, [open, defaultReferral, defaultType]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.terms) {
      setError('Bitte AGB / Risikohinweise akzeptieren');
      return;
    }
    const shareCount = parseInt(form.shareCount, 10);
    const amountCHF = parseFloat(form.amountCHF);
    if ((!shareCount || shareCount <= 0) && (!amountCHF || amountCHF <= 0)) {
      setError('Bitte Anzahl Aktien oder Betrag in CHF angeben');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(apiBase() + '/api/subscriptions/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shareCount: shareCount || null,
          amountCHF: amountCHF || null,
          shareType: form.shareType || 'namen',
          referralCode: form.referralCode || null,
          paymentMethod: form.paymentMethod,
          notes: form.notes || null,
          terms: form.terms,
        })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Zeichnung konnte nicht angelegt werden');
      toast.success('Zeichnung erfolgreich erfasst – wir bestätigen den Eingang per E-Mail');
      onCreated?.(data.subscription);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="subscription-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#111] border border-[#C4A55A]/40 rounded-2xl w-full max-w-lg p-6 md:p-8 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 text-[#C4A55A] text-xs uppercase tracking-wider mb-1">
              <Coins className="h-4 w-4" /> Aktien zeichnen
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white">Neue Zeichnung anlegen</h3>
            <p className="text-sm text-gray-400 mt-1">
              Aktien der Löwenherz Invest AG · Nennwert CHF 100 · Namen- und Vorzugs­aktien (Vorzugs­aktien in Planung)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            data-testid="sub-dialog-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-200 text-xs leading-relaxed">
            <strong>Hinweis:</strong> Aktien-Zeichnungen sind erst nach Eingang der Zahlung und
            Bestätigung durch die Geschäftsleitung verbindlich. Mindest­zeichnung: 30 Aktien (CHF 3 000)
            (CHF 100). Bitte beachten Sie die{' '}
            <a href="/risikohinweise" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-100">
              Risikohinweise
            </a>.
          </div>

          {/* Share type selector */}
          <div className="block">
            <span className="text-sm text-gray-300 block mb-2">Aktientyp</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(shareTypes && shareTypes.length > 0 ? shareTypes : [
                { value: 'namen', label: 'Namenaktien', enabled: true, description: 'Volle Stimmrechte' }
              ]).map((t) => {
                const selected = form.shareType === t.value;
                return (
                  <label
                    key={t.value}
                    data-testid={`share-type-${t.value}`}
                    className={`relative cursor-pointer rounded-lg border p-3 transition-all ${
                      !t.enabled
                        ? 'opacity-50 cursor-not-allowed border-white/10 bg-black/40'
                        : selected
                        ? 'border-[#C4A55A] bg-[#C4A55A]/10'
                        : 'border-white/15 bg-black/60 hover:border-[#C4A55A]/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shareType"
                      value={t.value}
                      checked={selected}
                      disabled={!t.enabled}
                      onChange={() => t.enabled && setForm({ ...form, shareType: t.value })}
                      className="sr-only"
                    />
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={`text-sm font-semibold ${selected ? 'text-[#C4A55A]' : 'text-white'}`}>
                        {t.label}
                      </span>
                      {!t.enabled && (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">
                          geplant
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 leading-snug">{t.description}</p>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-gray-300">Anzahl Aktien</span>
              <input
                type="number"
                min="30"
                step="1"
                value={form.shareCount}
                onChange={(e) => setForm({ ...form, shareCount: e.target.value, amountCHF: '' })}
                className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                placeholder="z. B. 50 (min. 30)"
                data-testid="sub-share-count"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-300">… oder Betrag CHF</span>
              <input
                type="number"
                min="3000"
                step="100"
                value={form.amountCHF}
                onChange={(e) => setForm({ ...form, amountCHF: e.target.value, shareCount: '' })}
                className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                placeholder="z. B. 5000 (min. 3000)"
                data-testid="sub-amount-chf"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm text-gray-300">Vermittler-Code (optional)</span>
            <input
              type="text"
              value={form.referralCode}
              onChange={(e) => setForm({ ...form, referralCode: e.target.value.toUpperCase() })}
              className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
              placeholder="LH-XXXX"
              data-testid="sub-referral"
            />
          </label>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-300">Zahlungsart:</span>
            <label className="text-sm text-gray-200 inline-flex items-center gap-1">
              <input
                type="radio"
                name="payment"
                checked={form.paymentMethod === 'bank'}
                onChange={() => setForm({ ...form, paymentMethod: 'bank' })}
              />{' '}
              Banküberweisung
            </label>
            <label className="text-sm text-gray-200 inline-flex items-center gap-1">
              <input
                type="radio"
                name="payment"
                checked={form.paymentMethod === 'other'}
                onChange={() => setForm({ ...form, paymentMethod: 'other' })}
              />{' '}
              Andere
            </label>
          </div>

          <label className="block">
            <span className="text-sm text-gray-300">Anmerkungen (optional)</span>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none resize-none"
            />
          </label>

          <label className="flex items-start gap-2 text-sm text-gray-200">
            <input
              type="checkbox"
              checked={form.terms}
              onChange={(e) => setForm({ ...form, terms: e.target.checked })}
              className="mt-1"
              data-testid="sub-terms"
            />
            <span>
              Ich habe die{' '}
              <a href="/risikohinweise" target="_blank" rel="noopener noreferrer" className="text-[#C4A55A] hover:text-[#D4B56A] underline">Risiko­hinweise</a>
              {' '}und die{' '}
              <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#C4A55A] hover:text-[#D4B56A] underline">AGB</a>
              {' '}gelesen und akzeptiere diese.
            </span>
          </label>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-[#1a1a1a] border border-white/10 hover:bg-white/5 text-gray-200 text-sm transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="sub-submit"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold disabled:opacity-50 transition-all"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
              Zeichnung absenden
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const SubscriptionsBlock = ({ defaultReferral }) => {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareTypes, setShareTypes] = useState([
    { value: 'namen', label: 'Namenaktien', enabled: true,
      description: 'Volle Stimm- und Mitwirkungsberechtigung an der Generalversammlung.' },
    { value: 'vorzugs', label: 'Vorzugsaktien', enabled: false,
      description: 'Bevorzugte Dividendenauszahlung (Aktuell noch in Planung).' },
  ]);

  const fetchSubs = async () => {
    setLoading(true);
    try {
      const r = await fetch(apiBase() + '/api/subscriptions/me', { credentials: 'include' });
      const data = await r.json();
      setSubs(data?.subscriptions || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const r = await fetch(apiBase() + '/api/subscriptions/config');
      const data = await r.json();
      if (data?.shareTypes && Array.isArray(data.shareTypes) && data.shareTypes.length > 0) {
        setShareTypes(data.shareTypes);
      }
    } catch {
      // keep defaults
    }
  };

  useEffect(() => { fetchSubs(); fetchConfig(); }, []);

  return (
    <section
      data-testid="subscriptions-block"
      className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8 mb-10"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-[#C4A55A]" />
          <h2 className="text-xl md:text-2xl font-bold">Meine Zeichnungen</h2>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="open-subscription-dialog"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold text-sm transition-all"
        >
          <Coins className="h-4 w-4" />
          Aktien zeichnen
        </button>
      </div>

      {/* 4-step process explanation */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        {[
          { n: '1', t: 'Zeichnung erfassen', d: 'Stückzahl oder Betrag online eingeben.' },
          { n: '2', t: 'PDF + E-Mail', d: 'Sie erhalten sofort eine Zeichnungs­bestätigung mit Bankdaten.' },
          { n: '3', t: 'Banküberweisung', d: 'Betrag innert 14 Tagen auf das angegebene Konto überweisen.' },
          { n: '4', t: 'Eintragung im Register', d: 'Nach Zahlungseingang werden Sie offiziell als Aktionär eingetragen.' },
        ].map((s) => (
          <div
            key={s.n}
            className="bg-black/40 border border-[#C4A55A]/15 rounded-xl p-3 hover:border-[#C4A55A]/40 transition-colors"
            data-testid={`sub-step-${s.n}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-[#C4A55A] text-black text-xs font-bold flex items-center justify-center">
                {s.n}
              </span>
              <span className="text-white text-sm font-semibold">{s.t}</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>

      {loading && <div className="text-gray-400 text-sm py-4">Lade Zeichnungen…</div>}

      {!loading && subs.length === 0 && (
        <div className="text-gray-400 text-sm py-4">
          Noch keine Zeichnungen. Klicken Sie auf <strong>„Aktien zeichnen"</strong> oben rechts, um eine neue Zeichnung anzulegen.
        </div>
      )}

      {!loading && subs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscriptions-table">
            <thead>
              <tr className="text-left text-gray-400 border-b border-white/10">
                <th className="py-3 pr-4 font-medium">Datum</th>
                <th className="py-3 pr-4 font-medium">Typ</th>
                <th className="py-3 pr-4 font-medium text-right">Aktien</th>
                <th className="py-3 pr-4 font-medium text-right">Betrag (CHF)</th>
                <th className="py-3 pr-4 font-medium">Vermittler</th>
                <th className="py-3 pr-4 font-medium">Status</th>
                <th className="py-3 pr-4 font-medium text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => {
                const meta = STATUS_LABEL[s.status] || STATUS_LABEL.pending;
                const Icon = meta.Icon;
                const typeLabel = (s.shareType || 'namen') === 'vorzugs' ? 'Vorzugs' : 'Namen';
                return (
                  <tr
                    key={s.id}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`sub-row-${s.id}`}
                  >
                    <td className="py-3 pr-4">{formatDate(s.createdAt)}</td>
                    <td className="py-3 pr-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-[#C4A55A]/10 text-[#C4A55A] border border-[#C4A55A]/30">
                        {typeLabel}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">{formatInt(s.shareCount)}</td>
                    <td className="py-3 pr-4 text-right tabular-nums text-[#C4A55A]">{formatCHF(s.amountCHF)}</td>
                    <td className="py-3 pr-4">{s.referralCode || '—'}</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${meta.cls}`}>
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <a
                        href={apiBase() + `/api/subscriptions/${s.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#C4A55A] hover:text-[#D4B56A] text-xs"
                        data-testid={`sub-pdf-${s.id}`}
                      >
                        <Download className="h-3.5 w-3.5" /> PDF
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4 flex items-center gap-1">
        <Receipt className="h-3 w-3" />
        Eingegangene Zeichnungen werden geprüft und sobald die Zahlung eingegangen ist im Aktienregister erfasst.
      </p>

      <SubscriptionDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => fetchSubs()}
        defaultReferral={defaultReferral}
        shareTypes={shareTypes}
      />
    </section>
  );
};
